import React, { useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Controller } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { SWIPE_BACK_EDGE_WIDTH } from '@/hooks/useSwipeBack';
import 'swiper/css';
import styles from './styles.module.scss';

export interface SwipeableTabContentProps {
  /** Array of content elements for each tab */
  children: React.ReactNode[];
  /** Current active tab index */
  activeIndex: number;
  /** Callback when slide changes (receives new index) */
  onSlideChange?: (index: number) => void;
  /** Navigation swiper instance to sync with */
  controlledSwiper?: SwiperType | null;
  /** Callback to expose this swiper instance */
  onSwiper?: (swiper: SwiperType) => void;
  /** Additional className */
  className?: string;
}

/**
 * SwipeableTabContent - Swipeable content container for tabbed pages
 *
 * Works with NavigationSwiper via Swiper Controller for two-way sync:
 * - Swipe content → tabs update
 * - Click tab → content slides
 *
 * Usage:
 * ```tsx
 * const [navSwiper, setNavSwiper] = useState<SwiperType | null>(null);
 * const [contentSwiper, setContentSwiper] = useState<SwiperType | null>(null);
 *
 * <NavigationSwiper
 *   controlledSwiper={contentSwiper}
 *   onSwiper={setNavSwiper}
 *   ...
 * />
 * <SwipeableTabContent
 *   controlledSwiper={navSwiper}
 *   onSwiper={setContentSwiper}
 *   activeIndex={activeTabIndex}
 *   onSlideChange={handleTabChange}
 * >
 *   <TabContent1 />
 *   <TabContent2 />
 * </SwipeableTabContent>
 * ```
 */
export const SwipeableTabContent: React.FC<SwipeableTabContentProps> = ({
  children,
  activeIndex,
  onSlideChange,
  controlledSwiper,
  onSwiper,
  className,
}) => {
  const [swiper, setSwiper] = React.useState<SwiperType | null>(null);

  // Handle swiper initialization
  const handleSwiper = (swiperInstance: SwiperType) => {
    setSwiper(swiperInstance);
    onSwiper?.(swiperInstance);
  };

  // Handle slide change
  const handleSlideChange = (swiperInstance: SwiperType) => {
    const newIndex = swiperInstance.activeIndex;
    onSlideChange?.(newIndex);
  };

  // Sync swiper position when activeIndex changes externally
  useEffect(() => {
    if (swiper && activeIndex !== undefined && swiper.activeIndex !== activeIndex) {
      swiper.slideTo(activeIndex, 300);
    }
  }, [swiper, activeIndex]);

  // Convert children to array
  const childrenArray = React.Children.toArray(children);

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <Swiper
        className={styles.swiper}
        modules={[Controller]}
        onSwiper={handleSwiper}
        onSlideChange={handleSlideChange}
        controller={controlledSwiper ? { control: controlledSwiper } : undefined}
        slidesPerView={1}
        spaceBetween={0}
        allowTouchMove={true}
        initialSlide={activeIndex}
        speed={300}
        touchStartPreventDefault={false}
        threshold={5}
        // Edge swipes belong to the app-wide swipe-back (useSwipeBack).
        edgeSwipeDetection={true}
        edgeSwipeThreshold={SWIPE_BACK_EDGE_WIDTH}
        noSwiping={true}
        noSwipingSelector=".select__control, .select__menu, .select__menu-list, .select__option, .select__input"
      >
        {childrenArray.map((child, index) => (
          <SwiperSlide key={index} className={styles.slide}>
            <div className={`${styles.slideContent} page-scroll`}>
              {child}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default SwipeableTabContent;
